import React from 'react';
import { TimeFrameValue } from 'common/types';
import EventsFeatureActionRow from 'pages/EventsTab/EventsFeatureActionRow';
import { useHistory } from 'react-router';

interface LeaderboardFeatureCardProps {
  durationOptions?: { label: string; value: TimeFrameValue }[];
  activeDuration?: TimeFrameValue;
  activeSlide?: number;
  onSlideChange?: (index: number) => void;
  onDurationChange?: (value: TimeFrameValue) => void;
  className?: string;
  showExploreButton?: boolean;
}

const LeaderboardFeatureCard: React.FC<LeaderboardFeatureCardProps> = ({
  durationOptions = [
    { label: '30 days', value: 'months1' },
    { label: '7 days', value: 'weeks1' },
    { label: '24 hours', value: 'hours24' },
  ],
  activeDuration = 'months1',
  activeSlide,
  onSlideChange,
  onDurationChange,
  className,
  showExploreButton = true,
}) => {
  const routerHistory = useHistory();

  const slides = React.useMemo(
    () => [
      {
        title: (
          <>
            MOST
            <br />
            JACKPOTS
          </>
        ),
        subtitle: 'Congratulations all you Jackpot Winners!',
      },
      {
        title: (
          <>
            MOST
            <br />$ WON
          </>
        ),
        subtitle: 'Congrats on your Big Winnings!',
      },
      {
        title: (
          <>
            MOST
            <br />
            SPINS
          </>
        ),
        subtitle: 'Congratulations all you Big Spinners!',
      },
      {
        title: (
          <>
            MOST $
            <br />
            WON / SPIN
          </>
        ),
        subtitle: 'Keep up that Spinning and Winning!',
      },
    ],
    []
  );

  const [internalActiveSlide, setInternalActiveSlide] = React.useState(0);
  const touchStartX = React.useRef<number | null>(null);
  const isControlled = typeof activeSlide === 'number';
  const currentSlide = isControlled ? activeSlide : internalActiveSlide;

  const currentBadgeImage = React.useMemo(() => {
    switch (currentSlide) {
      case 1:
        return '/assets/images/leader-moneywon-char.png';
      case 2:
        return null;
      case 3:
        return null;
      case 0:
        return '/assets/images/leaderboard-jkpt-char.png';
      default:
        return '/assets/images/leaderboard-spins-char.png';
    }
  }, [currentSlide]);

  const cardVariantClass = React.useMemo(() => {
    switch (currentSlide) {
      case 1:
        return 'leaderboard-card--money-won';
      case 2:
        return 'leaderboard-card--spins';
      case 3:
        return 'leaderboard-card--won-spins';
      case 0:
      default:
        return 'leaderboard-card--jkpt';
    }
  }, [currentSlide]);

  const setSlide = (index: number) => {
    const nextSlide = Math.max(0, Math.min(index, slides.length - 1));
    if (!isControlled) {
      setInternalActiveSlide(nextSlide);
    }
    onSlideChange?.(nextSlide);
  };

  const onTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
    touchStartX.current = e.changedTouches[0]?.clientX ?? null;
  };

  const onTouchEnd = (e: React.TouchEvent<HTMLDivElement>) => {
    if (touchStartX.current === null) return;
    const endX = e.changedTouches[0]?.clientX ?? touchStartX.current;
    const delta = endX - touchStartX.current;

    if (Math.abs(delta) >= 30) {
      setSlide(delta < 0 ? currentSlide + 1 : currentSlide - 1);
    }

    touchStartX.current = null;
  };

  const openFeaturedChallenge = () => {
    routerHistory.push('/tabs/home');
  };

  return (
    <div className="leaderboard-feature-slider">
      <div
        className={`leaderboard-card leaderboard-feature-card ${cardVariantClass} ${
          className ?? ''
        }`}
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
      >
        <div className="leaderboard-feature__content">
          <div className="leaderboard-feature__viewport">
            <div
              className="leaderboard-feature__track"
              style={{ transform: `translateX(-${currentSlide * 100}%)` }}
            >
              {slides.map((slide, index) => (
                <div className="leaderboard-feature__slide" key={index}>
                  <div className="leaderboard-feature__title">{slide.title}</div>
                  <div className="leaderboard-feature__line" />
                  <div className="leaderboard-feature__subtitle">{slide.subtitle}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="leaderboard-tabs">
            {durationOptions.map((option) => (
              <button
                key={option.value}
                className={
                  option.value === activeDuration
                    ? 'leaderboard-tabs__btn leaderboard-tabs__btn--active'
                    : 'leaderboard-tabs__btn'
                }
                onClick={() => onDurationChange?.(option.value)}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>
        <div
          className="leaderboard-feature__badge"
          aria-hidden="true"
          style={{ backgroundImage: `url('${currentBadgeImage}')` }}
        />
      </div>

      <div
        className={`leaderboard-feature__indicators${showExploreButton ? ' leaderboard-feature__indicators--has-explore' : ''}`}
        aria-label="Feature slide indicators"
      >
        <div className="leaderboard-feature__dots">
          {slides.map((_, index) => (
            <button
              key={index}
              aria-label={`Go to slide ${index + 1}`}
              className={
                index === currentSlide
                  ? 'leaderboard-feature__indicator leaderboard-feature__indicator--active'
                  : 'leaderboard-feature__indicator'
              }
              onClick={() => setSlide(index)}
            />
          ))}
        </div>

        {/* {showExploreButton && (
          <div className="leaderboard-feature__explore-row">
            <EventsFeatureActionRow onExploreClick={openFeaturedChallenge} showText={false} />
          </div>
        )} */}
      </div>
    </div>
  );
};

export default LeaderboardFeatureCard;
